import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  FIELDS,
  COLUMNS,
  FOAL_DAILY_FIELDS,
  FOAL_DAILY_COLUMNS,
  FOAL_FARRIER_COLUMNS,
  foalName,
  todayYMD,
  addDays,
  prettyDate,
  prettyDateShort,
  isToday,
  buildReportHtml,
  buildDailyRangeHtml,
  buildBredReportHtml,
  buildProceduresReportHtml,
  buildInFoalReportHtml,
  buildMareStatusReportHtml,
  buildBreedingScheduleReportHtml,
  buildFarrierReportHtml,
  printHtml,
  downloadText,
  combinedNotes,
  PROCEDURES,
  RIBBON_CYCLE,
  RIBBON_META,
  DEFAULT_SETTINGS,
} from "@/lib/equi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Printer,
  Download,
  Plus,
  Trash2,
  Loader2,
  FileText,
  HeartPulse,
  CalendarClock,
  RefreshCw,
} from "lucide-react";

export default function DailyRounds() {
  const { readOnly } = useAuth();
  const [date, setDate] = useState(todayYMD());
  const [mares, setMares] = useState([]);
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [mareChecks, setMareChecks] = useState(null); // row being edited
  const [saving, setSaving] = useState(false);
  const [addMareId, setAddMareId] = useState("");
  const [foals, setFoals] = useState([]);
  const [foalRows, setFoalRows] = useState([]);
  const [addFoalId, setAddFoalId] = useState("");
  const [editingFoal, setEditingFoal] = useState(null);
  const [foalFarrierView, setFoalFarrierView] = useState(false);
  const [farrierRows, setFarrierRows] = useState([]);
  const [addFarrierFoalId, setAddFarrierFoalId] = useState("");
  const [editingFarrier, setEditingFarrier] = useState(null);
  const [savingFarrier, setSavingFarrier] = useState(false);
  const [savingFoal, setSavingFoal] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState(todayYMD());
  const [rangeEnd, setRangeEnd] = useState(todayYMD());
  const [rangePrinting, setRangePrinting] = useState(false);
  // Breeding ribbon date picker
  const [breedOpen, setBreedOpen] = useState(false);
  const [breedMareId, setBreedMareId] = useState(null);
  const [breedDate, setBreedDate] = useState(todayYMD());
  const [ribbonBusy, setRibbonBusy] = useState(null);
  // Bred / Procedures reports
  const [reportKind, setReportKind] = useState(null); // "bred" | "procedures"
  const [repStart, setRepStart] = useState(addDays(todayYMD(), -10));
  const [repEnd, setRepEnd] = useState(todayYMD());
  const [repProcedure, setRepProcedure] = useState("__all__");
  const [repOnlyConfirmed, setRepOnlyConfirmed] = useState(false);
  const [repActiveOnly, setRepActiveOnly] = useState(true);
  const [repBreedingAsOf, setRepBreedingAsOf] = useState(todayYMD());
  const [repFarrierDate, setRepFarrierDate] = useState(todayYMD());
  const [repFarrierScheduledOnly, setRepFarrierScheduledOnly] = useState(true);
  const [repBusy, setRepBusy] = useState(false);
  // Oxytocin dose dialog
  const [oxyOpen, setOxyOpen] = useState(false);
  const [oxyForm, setOxyForm] = useState({ amount: "", route: "IM", times: "", days: "" });

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [m, r, s, fl, fr, far] = await Promise.all([
        api.get("/mares"),
        api.get(`/records/${date}`),
        api.get("/settings"),
        api.get("/foals"),
        api.get(`/foal-records/${date}`),
        api.get(`/farrier-records/${date}`),
      ]);
      setMares(m.data);
      setRows(r.data);
      setSettings(s.data);
      setFoals(fl.data);
      setFoalRows(fr.data);
      setFarrierRows(far.data);
    } catch {
      toast.error("Could not load today's records");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const byName = (a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
  const rosterIds = useMemo(() => new Set(rows.map((r) => r.mare_id)), [rows]);
  const available = mares.filter((m) => !rosterIds.has(m.id)).sort(byName);

  const foalRosterIds = useMemo(() => new Set(foalRows.map((r) => r.foal_id)), [foalRows]);
  const farrierRosterIds = useMemo(() => new Set(farrierRows.map((r) => r.foal_id)), [farrierRows]);
  const foalLabel = (f) => foalName(f);
  const availableFoals = foals
    .filter((f) => !foalRosterIds.has(f.id))
    .sort((a, b) => foalLabel(a).localeCompare(foalLabel(b), undefined, { sensitivity: "base" }));

  const availableFarrierFoals = foals
    .filter((f) => !farrierRosterIds.has(f.id))
    .sort((a, b) => foalLabel(a).localeCompare(foalLabel(b), undefined, { sensitivity: "base" }));

  const addFarrier = async () => {
    if (!addFarrierFoalId) return;
    try {
      await api.post(`/farrier-records/${date}/foal/${addFarrierFoalId}`);
      setAddFarrierFoalId("");
      load(true);
    } catch {
      toast.error("Failed to add foal");
    }
  };

  const removeFarrier = async (foalId) => {
    try {
      await api.delete(`/farrier-records/${date}/foal/${foalId}`);
      load(true);
    } catch {
      toast.error("Failed to remove foal");
    }
  };

  const saveFarrierEdit = async () => {
    if (!editingFarrier) return;
    setSavingFarrier(true);
    try {
      await api.put("/farrier-records", {
        foal_id: editingFarrier.foal_id,
        date,
        notes: editingFarrier.notes || "",
        next_due: editingFarrier.next_due || "",
      });
      toast.success("Saved");
      setEditingFarrier(null);
      load(true);
    } catch {
      toast.error("Failed to save farrier record");
    } finally {
      setSavingFarrier(false);
    }
  };

  const addFoal = async () => {
    if (!addFoalId) return;
    try {
      await api.post(`/foal-records/${date}/foal/${addFoalId}`);
      setAddFoalId("");
      load(true);
    } catch {
      toast.error("Failed to add foal");
    }
  };

  const removeFoal = async (foalId) => {
    try {
      await api.delete(`/foal-records/${date}/foal/${foalId}`);
      load(true);
    } catch {
      toast.error("Failed to remove foal");
    }
  };

  const saveFoalEdit = async () => {
    if (!editingFoal) return;
    setSavingFoal(true);
    try {
      await api.put("/foal-records", {
        foal_id: editingFoal.foal_id,
        date,
        physical_exam: editingFoal.physical_exam || "",
        umbilicus: editingFoal.umbilicus || "",
        medication: editingFoal.medication || "",
        bandages: editingFoal.bandages || "",
        notes: editingFoal.notes || "",
        additional_procedures: editingFoal.additional_procedures || "",
        next_check: editingFoal.next_check || "",
      });
      // Farrier is a separate log — write it independently so the exam row and
      // the farrier record never share (or delete) each other's data.
      if ((editingFoal.farrier_notes || "").trim() || (editingFoal.farrier_next_due || "").trim()) {
        await api.put("/farrier-records", {
          foal_id: editingFoal.foal_id,
          date,
          notes: editingFoal.farrier_notes || "",
          next_due: editingFoal.farrier_next_due || "",
        });
      }
      toast.success("Saved");
      setEditingFoal(null);
      load(true);
    } catch {
      toast.error("Failed to save foal record");
    } finally {
      setSavingFoal(false);
    }
  };

  const addMare = async () => {
    if (!addMareId) return;
    try {
      await api.post(`/records/${date}/mare/${addMareId}`);
      setAddMareId("");
      load(true);
    } catch {
      toast.error("Failed to add mare");
    }
  };

  const removeMare = async (mareId) => {
    try {
      await api.delete(`/records/${date}/mare/${mareId}`);
      load(true);
    } catch {
      toast.error("Failed to remove mare");
    }
  };

  const openEdit = async (row) => {
    const m = mares.find((x) => x.id === row.mare_id);
    const conf = (m?.confirmed_in_foal_date || "").trim();
    setEditing({ ...row, in_foal: !!conf, _confirmed: conf });
    setMareChecks(null);
    try {
      const { data } = await api.get(`/mares/${row.mare_id}/infoal-checks?as_of=${date}`);
      setMareChecks(data);
    } catch {
      setMareChecks(null);
    }
  };

  const toggleCheckDone = async (which, done) => {
    if (!editing) return;
    try {
      await api.put(`/mares/${editing.mare_id}/infoal-checks`, { which, done });
      const { data } = await api.get(`/mares/${editing.mare_id}/infoal-checks?as_of=${date}`);
      setMareChecks(data);
    } catch {
      toast.error("Failed to update check");
    }
  };

  const syncInFoalChecks = async () => {
    try {
      const { data } = await api.post("/mares/sync-infoal-checks");
      toast.success(`Synced 28d / 45d checks for ${data.synced_mares} confirmed mare${data.synced_mares === 1 ? "" : "s"}`);
      load(true);
    } catch {
      toast.error("Failed to sync in-foal checks");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      // "In Foal" sets the Confirmed In Foal date and the backend ensures the
      // 28d/45d re-checks exist. Always send the date while checked so re-saving
      // a mare self-heals any missing check rows; clearing it removes upcoming ones.
      const marePayload = { stallion: editing.stallion || "" };
      let confirmedNow = false;
      if (editing.in_foal) {
        marePayload.confirmed_in_foal_date = editing._confirmed || date;
        confirmedNow = !editing._confirmed;
      } else if (!editing.in_foal && editing._confirmed) {
        marePayload.confirmed_in_foal_date = "";
      }
      await api.put(`/mares/${editing.mare_id}`, marePayload);
      await api.put("/records", {
        mare_id: editing.mare_id,
        date,
        left_ovary: editing.left_ovary || "",
        right_ovary: editing.right_ovary || "",
        fluid: editing.fluid || "",
        edema: editing.edema || "",
        cervix: editing.cervix || "",
        notes: editing.notes || "",
        next_check: editing.next_check || "",
        breeding_status: editing.breeding_status || "",
        procedures: editing.procedures || [],
        procedure_details: editing.procedure_details || {},
      });
      toast.success(confirmedNow ? "Confirmed in foal — 28 / 45-day checks scheduled" : "Saved");
      setEditing(null);
      load(true);
    } catch {
      toast.error("Failed to save record");
    } finally {
      setSaving(false);
    }
  };

  const cycleRibbon = async (row, e) => {
    e.stopPropagation();
    if (readOnly) return;
    const cur = row.breeding_status || "";
    const next = RIBBON_CYCLE[(RIBBON_CYCLE.indexOf(cur) + 1) % RIBBON_CYCLE.length];
    setRibbonBusy(row.mare_id);
    try {
      await api.put(`/records/${date}/mare/${row.mare_id}/breeding-status`, { status: next });
      await load(true);
      if (next === "ready") {
        setBreedMareId(row.mare_id);
        setBreedDate(date);
        setBreedOpen(true);
      }
    } catch {
      toast.error("Could not update breeding status");
    } finally {
      setRibbonBusy(null);
    }
  };

  const confirmBreed = async () => {
    if (!breedMareId) return;
    setRibbonBusy(breedMareId);
    try {
      await api.post("/records/breed", { mare_id: breedMareId, date: breedDate });
      toast.success("Bred date scheduled — infusion added the next day");
      setBreedOpen(false);
      load(true);
    } catch (e) {
      toast.error("Could not schedule the breed date");
    } finally {
      setRibbonBusy(null);
    }
  };

  const toggleProcedure = (p) => {
    if (p === "Oxytocin") {
      openOxy();
      return;
    }
    setEditing((s) => {
      const cur = s.procedures || [];
      return { ...s, procedures: cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p] };
    });
  };

  const openOxy = () => {
    const d = editing?.procedure_details?.Oxytocin || {};
    setOxyForm({ amount: d.amount || "", route: d.route || "IM", times: d.times || "", days: d.days || "" });
    setOxyOpen(true);
  };

  const saveOxy = () => {
    setEditing((s) => {
      const procs = s.procedures || [];
      const details = { ...(s.procedure_details || {}) };
      details.Oxytocin = { ...oxyForm };
      return {
        ...s,
        procedures: procs.includes("Oxytocin") ? procs : [...procs, "Oxytocin"],
        procedure_details: details,
      };
    });
    setOxyOpen(false);
  };

  const removeOxy = () => {
    setEditing((s) => {
      const details = { ...(s.procedure_details || {}) };
      delete details.Oxytocin;
      return { ...s, procedures: (s.procedures || []).filter((x) => x !== "Oxytocin"), procedure_details: details };
    });
    setOxyOpen(false);
  };

  const runReport = async () => {
    if (repStart > repEnd) {
      toast.error("Start date must be on or before the end date");
      return;
    }
    setRepBusy(true);
    try {
      if (reportKind === "bred") {
        const { data } = await api.get(`/reports/bred?start=${repStart}&end=${repEnd}${repOnlyConfirmed ? "&only_confirmed=true" : ""}`);
        printHtml(buildBredReportHtml(data, settings, repStart, repEnd));
      } else if (reportKind === "infoal") {
        const { data } = await api.get(`/reports/in-foal?only_confirmed=${repOnlyConfirmed}`);
        printHtml(buildInFoalReportHtml(data, settings));
      } else if (reportKind === "status") {
        const { data } = await api.get("/reports/mare-status");
        const rows = (data || []).map((r) => ({ ...r, notes: combinedNotes(r) }));
        printHtml(buildMareStatusReportHtml(rows, settings));
      } else if (reportKind === "breeding") {
        const { data } = await api.get(`/reports/breeding-schedule?as_of=${repBreedingAsOf}`);
        const fmt = (d) => (d ? prettyDate(d) : "");
        const rows = (data || [])
          .filter((r) => !repActiveOnly || r.active)
          .map((r) => ({
            ...r,
            bred_date: fmt(r.bred_date),
            ov_date: fmt(r.ov_date),
            check_14: fmt(r.check_14),
            check_28: fmt(r.check_28),
            check_45: fmt(r.check_45),
          }));
        printHtml(buildBreedingScheduleReportHtml(rows, settings, repBreedingAsOf));
      } else if (reportKind === "farrier") {
        const { data } = await api.get(`/reports/farrier?date=${repFarrierDate}&scheduled_only=${repFarrierScheduledOnly}`);
        const fmt = (d) => (d ? prettyDate(d) : "");
        const fmtAge = (dobStr, days) => {
          if (typeof days !== "number") return "—";
          if (days < 120) return `${days} day${days === 1 ? "" : "s"}`;
          const dob = new Date(`${dobStr}T00:00:00`);
          const asOf = new Date(`${repFarrierDate}T00:00:00`);
          if (Number.isNaN(dob.getTime()) || Number.isNaN(asOf.getTime())) return `${days} days`;
          let months = (asOf.getFullYear() - dob.getFullYear()) * 12 + (asOf.getMonth() - dob.getMonth());
          let d = asOf.getDate() - dob.getDate();
          if (d < 0) {
            months -= 1;
            d += new Date(asOf.getFullYear(), asOf.getMonth(), 0).getDate();
          }
          return `${months}m ${d}d`;
        };
        const rows = (data || []).map((r) => ({
          ...r,
          dob: r.foal_out_date ? prettyDate(r.foal_out_date) : "—",
          age: fmtAge(r.foal_out_date, r.age_days),
          last_visit: fmt(r.last_visit),
        }));
        printHtml(buildFarrierReportHtml(rows, settings, repFarrierDate));
      } else {
        const proc = repProcedure === "__all__" ? "" : repProcedure;
        const url = `/reports/procedures?start=${repStart}&end=${repEnd}${proc ? `&procedure=${encodeURIComponent(proc)}` : ""}`;
        const { data } = await api.get(url);
        printHtml(buildProceduresReportHtml(data, settings, repStart, repEnd, proc));
      }
      setReportKind(null);
    } catch {
      toast.error("Could not build the report");
    } finally {
      setRepBusy(false);
    }
  };

  const handlePrint = () => {
    printHtml(buildReportHtml(date, rows, settings, "daily", foalRows));
  };

  const handlePrintRange = async () => {
    if (rangeStart > rangeEnd) {
      toast.error("Start date must be on or before the end date");
      return;
    }
    // Guard against very large ranges (keeps the print job reasonable).
    const maxDays = 92;
    const dates = [];
    for (let d = rangeStart; d <= rangeEnd && dates.length <= maxDays; d = addDays(d, 1)) {
      dates.push(d);
    }
    if (dates.length > maxDays) {
      toast.error(`Please choose a range of ${maxDays} days or fewer`);
      return;
    }
    setRangePrinting(true);
    try {
      const days = await Promise.all(
        dates.map(async (d) => {
          const [rec, foalRec] = await Promise.all([
            api.get(`/records/${d}`),
            api.get(`/foal-records/${d}`),
          ]);
          return { date: d, rows: rec.data, foalRows: foalRec.data };
        })
      );
      printHtml(buildDailyRangeHtml(days, settings));
      setRangeOpen(false);
    } catch (e) {
      toast.error("Could not build the range report");
    } finally {
      setRangePrinting(false);
    }
  };

  const handleCsv = async () => {
    try {
      const { data } = await api.get(`/export/${date}/csv`);
      downloadText(`daily-rounds-${date}.csv`, data);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Daily Rounds</h1>
          <p className="text-muted-foreground text-sm">Log each mare&apos;s exam for the selected day.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCsv} data-testid="daily-csv-btn" className="gap-2">
            <Download className="h-4 w-4" /> CSV
          </Button>
          <Button size="sm" onClick={handlePrint} data-testid="daily-print-btn" className="gap-2">
            <Printer className="h-4 w-4" /> Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRangeStart(date);
              setRangeEnd(date);
              setRangeOpen(true);
            }}
            data-testid="daily-print-range-btn"
            className="gap-2"
          >
            <CalendarDays className="h-4 w-4" /> Print Range
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="daily-reports-btn">
                <FileText className="h-4 w-4" /> Reports
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => { setReportKind("bred"); }}
                data-testid="report-bred-open"
              >
                <HeartPulse className="h-4 w-4 mr-2 text-red-500" /> Cover Dates
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setRepProcedure("__all__"); setReportKind("procedures"); }}
                data-testid="report-procedures-open"
              >
                <FileText className="h-4 w-4 mr-2 text-primary" /> Procedures
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setRepOnlyConfirmed(true); setReportKind("infoal"); }}
                data-testid="report-infoal-open"
              >
                <HeartPulse className="h-4 w-4 mr-2 text-emerald-600" /> In-Foal Summary
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setReportKind("status"); }}
                data-testid="report-status-open"
              >
                <FileText className="h-4 w-4 mr-2 text-primary" /> Mare Status
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setReportKind("breeding"); }}
                data-testid="report-breeding-open"
              >
                <CalendarClock className="h-4 w-4 mr-2 text-primary" /> Breeding &amp; Ovulation Schedule
              </DropdownMenuItem>
              <DropdownMenuItem onClick={syncInFoalChecks} data-testid="sync-infoal-checks">
                <RefreshCw className="h-4 w-4 mr-2 text-emerald-600" /> Sync In-Foal Checks
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setRepFarrierDate(todayYMD()); setReportKind("farrier"); }}
                data-testid="report-farrier-open"
              >
                <FileText className="h-4 w-4 mr-2 text-primary" /> Farrier Report (Foals)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Print date-range dialog */}
      <Dialog open={rangeOpen} onOpenChange={setRangeOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Print Daily Rounds — Date Range</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            Prints one Daily Rounds chart per day across the selected range.
          </p>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="range-start">From</Label>
              <Input
                id="range-start"
                type="date"
                value={rangeStart}
                onChange={(e) => e.target.value && setRangeStart(e.target.value)}
                data-testid="range-start-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="range-end">To</Label>
              <Input
                id="range-end"
                type="date"
                value={rangeEnd}
                onChange={(e) => e.target.value && setRangeEnd(e.target.value)}
                data-testid="range-end-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRangeOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePrintRange} disabled={rangePrinting} data-testid="range-print-confirm-btn" className="gap-2">
              {rangePrinting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date navigator */}
      <div className="flex items-center justify-between gap-3 bg-card border border-border rounded-xl p-3 mb-5">
        <Button variant="ghost" size="icon" onClick={() => setDate(addDays(date, -1))} data-testid="daily-prev-day">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-3">
          <CalendarDays className="h-5 w-5 text-primary" />
          <div className="text-center">
            <p className="font-bold text-lg leading-none">{prettyDate(date)}</p>
            {isToday(date) && <span className="text-xs text-primary font-medium">Today</span>}
          </div>
          <Input
            type="date"
            value={date}
            onChange={(e) => e.target.value && setDate(e.target.value)}
            className="w-40"
            data-testid="daily-date-input"
          />
          {!isToday(date) && (
            <Button variant="outline" size="sm" onClick={() => setDate(todayYMD())} data-testid="daily-today-btn">
              Today
            </Button>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={() => setDate(addDays(date, 1))} data-testid="daily-next-day">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Add mare */}
      {!readOnly && (
        <div className="flex items-center gap-2 mb-4">
          <Select value={addMareId} onValueChange={setAddMareId}>
            <SelectTrigger className="w-64" data-testid="daily-add-mare-select">
              <SelectValue placeholder="Add a mare to this day…" />
            </SelectTrigger>
            <SelectContent>
              {available.length === 0 ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">All mares added</div>
              ) : (
                available.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Button onClick={addMare} disabled={!addMareId} className="gap-2" data-testid="daily-add-mare-btn">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      )}

      {/* Ribbon legend */}
      {!loading && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-4 mb-3 text-xs text-muted-foreground" data-testid="ribbon-legend">
          <span className="font-semibold uppercase tracking-wide">Breeding:</span>
          {["ready", "bred", "infuse"].map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <span className="h-3.5 w-3.5 rounded-full border-2" style={{ backgroundColor: RIBBON_META[k].color, borderColor: RIBBON_META[k].border }} />
              {RIBBON_META[k].label}
            </span>
          ))}
          <span className="text-muted-foreground/70">· Tap the dot by a mare to cycle</span>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl">
          <p className="text-lg font-semibold">No mares on the roster for this day</p>
          <p className="text-muted-foreground text-sm mt-1">Use the selector above to add a mare and start logging.</p>
        </div>
      ) : (
        <div className="border border-[#C4C8D6] rounded-md bg-card overflow-x-auto equi-scroll">
          <table className="w-full text-[13px] min-w-[900px]">
            <thead>
              <tr className="bg-primary text-primary-foreground text-[11px] uppercase tracking-wide [&>th]:px-3 [&>th]:py-2.5 [&>th]:text-left [&>th]:font-semibold [&>th]:border-r [&>th]:border-white/15 last:[&>th]:border-r-0">
                <th className="w-10">#</th>
                <th className="sticky left-0 bg-primary">Mare</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} className="whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={r.mare_id}
                  className={`border-b border-[#C4C8D6] hover:bg-primary/5 transition-colors [&>td]:px-3 [&>td]:py-2 [&>td]:border-r [&>td]:border-[#C4C8D6] last:[&>td]:border-r-0 ${readOnly ? "" : "cursor-pointer"}`}
                  onClick={() => !readOnly && openEdit(r)}
                  data-testid={`daily-row-${r.mare_id}`}
                >
                  <td className="text-muted-foreground/60 font-mono text-xs">{i + 1}</td>
                  <td className="font-semibold sticky left-0 bg-card whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        title={RIBBON_META[r.breeding_status || ""].label}
                        onClick={(e) => cycleRibbon(r, e)}
                        disabled={readOnly || ribbonBusy === r.mare_id}
                        data-testid={`ribbon-${r.mare_id}`}
                        className="h-5 w-5 rounded-full border-2 shrink-0 transition-transform hover:scale-110 disabled:opacity-60"
                        style={{
                          backgroundColor: RIBBON_META[r.breeding_status || ""].color,
                          borderColor: RIBBON_META[r.breeding_status || ""].border,
                        }}
                      />
                      <span>{r.mare_name}</span>
                    </div>
                    {r.prev?.date && (
                      <div className="text-[10px] italic font-normal text-muted-foreground/60 leading-tight mt-0.5 ml-7" data-testid={`daily-prev-date-${r.mare_id}`}>
                        prev {prettyDateShort(r.prev.date)}
                      </div>
                    )}
                  </td>
                  {COLUMNS.map((c) => {
                    let val = r[c.key] || "";
                    if (c.key === "notes") val = combinedNotes(r);
                    if (c.key === "next_check" && val) val = prettyDateShort(val);
                    const numeric = ["left_ovary", "right_ovary", "edema"].includes(c.key);
                    const showPrev = r.prev && c.key !== "stallion" && c.key !== "next_check";
                    let prevVal = showPrev ? (c.key === "notes" ? combinedNotes(r.prev) : (r.prev[c.key] || "")) : "";
                    return (
                      <td
                        key={c.key}
                        className={`${c.key === "notes" ? "italic text-muted-foreground max-w-[220px]" : ""} ${
                          numeric ? "font-mono" : ""
                        } ${c.key === "next_check" ? "text-primary font-semibold font-mono whitespace-nowrap" : ""}`}
                      >
                        <div className={c.key === "notes" ? "truncate" : ""}>{val || <span className="text-muted-foreground/30">—</span>}</div>
                        {prevVal && (
                          <div className="text-[10px] italic text-muted-foreground/60 leading-tight mt-0.5 truncate max-w-[200px]">{prevVal}</div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => removeMare(r.mare_id)}
                        data-testid={`daily-remove-${r.mare_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Foals */}
      <div className="mt-10">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-xl font-extrabold tracking-tight">Foals</h2>
          <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1" data-testid="foal-view-toggle">
            <Button
              variant={foalFarrierView ? "ghost" : "default"}
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => setFoalFarrierView(false)}
              data-testid="foal-view-exam"
            >
              Exam
            </Button>
            <Button
              variant={foalFarrierView ? "default" : "ghost"}
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => setFoalFarrierView(true)}
              data-testid="foal-view-farrier"
            >
              Farrier
            </Button>
          </div>
        </div>
        {!foalFarrierView ? (
          <>
            {!readOnly && (
              <div className="flex items-center gap-2 mb-4">
                <Select value={addFoalId} onValueChange={setAddFoalId}>
                  <SelectTrigger className="w-72" data-testid="daily-add-foal-select">
                    <SelectValue placeholder="Add a foal to this day…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFoals.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No foals available</div>
                    ) : (
                      availableFoals.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{foalLabel(f)}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button onClick={addFoal} disabled={!addFoalId} className="gap-2" data-testid="daily-add-foal-btn">
                  <Plus className="h-4 w-4" /> Add
                </Button>
              </div>
            )}

            {foalRows.length === 0 ? (
              <div className="text-center py-12 bg-card border border-dashed border-border rounded-xl">
                <p className="font-semibold">No foals on the roster for this day</p>
                <p className="text-muted-foreground text-sm mt-1">Register a foal (set a Foal Out Date on a breeding card), then add it here.</p>
              </div>
            ) : (
              <div className="border border-[#C4C8D6] rounded-md bg-card overflow-x-auto equi-scroll">
                <table className="w-full text-[13px] min-w-[900px]">
                  <thead>
                    <tr className="bg-primary text-primary-foreground text-[11px] uppercase tracking-wide [&>th]:px-3 [&>th]:py-2.5 [&>th]:text-left [&>th]:font-semibold [&>th]:border-r [&>th]:border-white/15 last:[&>th]:border-r-0">
                      <th className="w-10">#</th>
                      <th className="sticky left-0 bg-primary">Foal (Dam)</th>
                      {FOAL_DAILY_COLUMNS.map((c) => (
                        <th key={c.key} className="whitespace-nowrap">{c.label}</th>
                      ))}
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {foalRows.map((r, i) => (
                      <tr
                        key={r.foal_id}
                        className={`border-b border-[#C4C8D6] hover:bg-primary/5 transition-colors [&>td]:px-3 [&>td]:py-2 [&>td]:border-r [&>td]:border-[#C4C8D6] last:[&>td]:border-r-0 ${readOnly ? "" : "cursor-pointer"}`}
                        onClick={() => !readOnly && setEditingFoal({ ...r })}
                        data-testid={`daily-foal-row-${r.foal_id}`}
                      >
                        <td className="text-muted-foreground/60 font-mono text-xs">{i + 1}</td>
                        <td className="font-semibold sticky left-0 bg-card whitespace-nowrap">
                          {foalName(r)}
                        </td>
                        {FOAL_DAILY_COLUMNS.map((c) => {
                          let val = r[c.key] || "";
                          if (c.key === "next_check" && val) val = prettyDateShort(val);
                          return (
                            <td
                              key={c.key}
                              className={`${c.key === "notes" || c.key === "additional_procedures" ? "italic text-muted-foreground max-w-[200px] truncate" : ""} ${
                                c.key === "next_check" ? "text-primary font-semibold font-mono whitespace-nowrap" : ""
                              }`}
                            >
                              {val || <span className="text-muted-foreground/30">—</span>}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          {!readOnly && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => removeFoal(r.foal_id)}
                              data-testid={`daily-foal-remove-${r.foal_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-xs text-muted-foreground -mt-1 mb-3">
              Farrier is a separate log — adding or removing a foal here does not affect the Exam list or that foal&apos;s exam records.
            </p>
            {!readOnly && (
              <div className="flex items-center gap-2 mb-4">
                <Select value={addFarrierFoalId} onValueChange={setAddFarrierFoalId}>
                  <SelectTrigger className="w-72" data-testid="daily-add-farrier-select">
                    <SelectValue placeholder="Add a foal to the farrier list…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFarrierFoals.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No foals available</div>
                    ) : (
                      availableFarrierFoals.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{foalLabel(f)}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button onClick={addFarrier} disabled={!addFarrierFoalId} className="gap-2" data-testid="daily-add-farrier-btn">
                  <Plus className="h-4 w-4" /> Add
                </Button>
              </div>
            )}

            {farrierRows.length === 0 ? (
              <div className="text-center py-12 bg-card border border-dashed border-border rounded-xl" data-testid="farrier-empty">
                <p className="font-semibold">No foals on the farrier list for this day</p>
                <p className="text-muted-foreground text-sm mt-1">Add a foal above to log a farrier visit.</p>
              </div>
            ) : (
              <div className="border border-[#C4C8D6] rounded-md bg-card overflow-x-auto equi-scroll">
                <table className="w-full text-[13px] min-w-[700px]">
                  <thead>
                    <tr className="bg-primary text-primary-foreground text-[11px] uppercase tracking-wide [&>th]:px-3 [&>th]:py-2.5 [&>th]:text-left [&>th]:font-semibold [&>th]:border-r [&>th]:border-white/15 last:[&>th]:border-r-0">
                      <th className="w-10">#</th>
                      <th className="sticky left-0 bg-primary">Foal (Dam)</th>
                      {FOAL_FARRIER_COLUMNS.map((c) => (
                        <th key={c.key} className="whitespace-nowrap">{c.label}</th>
                      ))}
                      <th className="w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {farrierRows.map((r, i) => (
                      <tr
                        key={r.foal_id}
                        className={`border-b border-[#C4C8D6] hover:bg-primary/5 transition-colors [&>td]:px-3 [&>td]:py-2 [&>td]:border-r [&>td]:border-[#C4C8D6] last:[&>td]:border-r-0 ${readOnly ? "" : "cursor-pointer"}`}
                        onClick={() => !readOnly && setEditingFarrier({ ...r })}
                        data-testid={`daily-farrier-row-${r.foal_id}`}
                      >
                        <td className="text-muted-foreground/60 font-mono text-xs">{i + 1}</td>
                        <td className="font-semibold sticky left-0 bg-card whitespace-nowrap">
                          {foalName(r)}
                        </td>
                        {FOAL_FARRIER_COLUMNS.map((c) => {
                          let val = r[c.key] || "";
                          if (c.key === "next_due" && val) val = prettyDateShort(val);
                          return (
                            <td
                              key={c.key}
                              className={`${c.key === "notes" ? "italic text-muted-foreground max-w-[260px] truncate" : ""} ${
                                c.key === "next_due" ? "text-primary font-semibold font-mono whitespace-nowrap" : ""
                              }`}
                            >
                              {val || <span className="text-muted-foreground/30">—</span>}
                            </td>
                          );
                        })}
                        <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          {!readOnly && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => removeFarrier(r.foal_id)}
                              data-testid={`daily-farrier-remove-${r.foal_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.mare_name} · {prettyDate(date)}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-4 py-2">
              {FIELDS.map((f) => (
                <div key={f.key} className={`space-y-1.5 ${f.multiline ? "col-span-2" : ""}`}>
                  <Label htmlFor={`f-${f.key}`}>{f.label}</Label>
                  {f.multiline ? (
                    <Textarea
                      id={`f-${f.key}`}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      data-testid={`edit-field-${f.key}`}
                    />
                  ) : (
                    <Input
                      id={`f-${f.key}`}
                      type={f.kind === "date" ? "date" : "text"}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      data-testid={`edit-field-${f.key}`}
                    />
                  )}
                  {f.key === "next_check" && (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {[3, 7, 14, 21].map((n) => (
                        <Button
                          key={n}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2.5 text-xs font-mono"
                          onClick={() => setEditing((s) => ({ ...s, next_check: addDays(date, n) }))}
                          data-testid={`next-check-plus-${n}`}
                        >
                          +{n}d
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="col-span-2 flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2.5">
                <div className="min-w-0">
                  <Label htmlFor="edit-in-foal" className="cursor-pointer">Confirmed In Foal</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {editing.in_foal
                      ? (() => {
                          const c28 = mareChecks?.checks?.find((c) => c.key === "28")?.date;
                          const c45 = mareChecks?.checks?.find((c) => c.key === "45")?.date;
                          return `Confirmed ${prettyDateShort(editing._confirmed || date)}${c28 ? ` · 28d check ${prettyDateShort(c28)}` : ""}${c45 ? ` · 45d check ${prettyDateShort(c45)}` : ""}${c28 || c45 ? " (from OV date)" : ""}`;
                        })()
                      : "Check to set the Confirmed In Foal date to this day; the 28 / 45-day re-checks are scheduled from the OV date."}
                  </p>
                </div>
                <Checkbox
                  id="edit-in-foal"
                  checked={!!editing.in_foal}
                  onCheckedChange={(v) => setEditing((s) => ({ ...s, in_foal: !!v }))}
                  data-testid="edit-in-foal-checkbox"
                  className="mt-1"
                />
              </div>
              {mareChecks?.bred && (
                <div className="col-span-2 space-y-2 rounded-md border border-border px-3 py-2.5" data-testid="infoal-checks">
                  <Label>In-Foal Checks <span className="text-muted-foreground font-normal">(tick when completed)</span></Label>
                  <div className="space-y-1.5">
                    {mareChecks.checks.map((c) => (
                      <div key={c.key} className="flex items-center justify-between gap-3">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={c.done}
                            onCheckedChange={(v) => toggleCheckDone(c.key, !!v)}
                            data-testid={`check-done-${c.key}`}
                          />
                          <span className="font-semibold">{c.days}d check</span>
                          <span className="text-muted-foreground font-mono text-xs">{c.date ? prettyDateShort(c.date) : "—"}</span>
                        </label>
                        <span
                          className={`text-xs font-bold ${
                            c.done ? "text-muted-foreground"
                            : c.status === "overdue" ? "text-red-600"
                            : c.status === "due" ? "text-amber-600"
                            : "text-muted-foreground"
                          }`}
                        >
                          {c.done
                            ? `Done ${c.done_date ? prettyDateShort(c.done_date) : ""}`
                            : c.status === "overdue" ? "Overdue"
                            : c.status === "due" ? "Due this week"
                            : "Upcoming"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="col-span-2 space-y-2 pt-1">
                <Label>Procedures <span className="text-muted-foreground font-normal">(tap to add to Notes)</span></Label>
                <div className="flex flex-wrap gap-1.5" data-testid="procedure-chips">
                  {PROCEDURES.map((p) => {
                    const on = (editing.procedures || []).includes(p);
                    return (
                      <Button
                        key={p}
                        type="button"
                        variant={on ? "default" : "outline"}
                        size="sm"
                        className="h-7 px-3 text-xs"
                        onClick={() => toggleProcedure(p)}
                        data-testid={`procedure-${p}`}
                      >
                        {p}
                      </Button>
                    );
                  })}
                </div>
                {(editing.procedures || []).length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    In Notes: <span className="italic">{combinedNotes(editing) || "—"}</span>
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Foal edit dialog */}
      <Dialog open={!!editingFoal} onOpenChange={(o) => !o && setEditingFoal(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFoal ? `${foalName(editingFoal)} · ${prettyDate(date)}` : ""}</DialogTitle>
          </DialogHeader>
          {editingFoal && (
            <div className="grid grid-cols-2 gap-4 py-2">
              {FOAL_DAILY_FIELDS.map((f) => (
                <div key={f.key} className={`space-y-1.5 ${f.multiline ? "col-span-2" : ""}`}>
                  <Label htmlFor={`ff-${f.key}`}>{f.label}</Label>
                  {f.multiline ? (
                    <Textarea
                      id={`ff-${f.key}`}
                      value={editingFoal[f.key] || ""}
                      onChange={(e) => setEditingFoal((s) => ({ ...s, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      data-testid={`foal-edit-${f.key}`}
                    />
                  ) : (
                    <Input
                      id={`ff-${f.key}`}
                      type={f.kind === "date" ? "date" : "text"}
                      value={editingFoal[f.key] || ""}
                      onChange={(e) => setEditingFoal((s) => ({ ...s, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      data-testid={`foal-edit-${f.key}`}
                    />
                  )}
                  {f.key === "next_check" && (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {[3, 7, 14, 21].map((n) => (
                        <Button
                          key={n}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2.5 text-xs font-mono"
                          onClick={() => setEditingFoal((s) => ({ ...s, next_check: addDays(date, n) }))}
                          data-testid={`foal-next-check-plus-${n}`}
                        >
                          +{n}d
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingFoal(null)}>Cancel</Button>
            <Button onClick={saveFoalEdit} disabled={savingFoal} data-testid="foal-edit-save-btn">
              {savingFoal && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Farrier edit dialog */}
      <Dialog open={!!editingFarrier} onOpenChange={(o) => !o && setEditingFarrier(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingFarrier ? `${foalName(editingFarrier)} · Farrier · ${prettyDate(date)}` : ""}</DialogTitle>
          </DialogHeader>
          {editingFarrier && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="far-notes">Farrier Notes</Label>
                <Textarea
                  id="far-notes"
                  value={editingFarrier.notes || ""}
                  placeholder="Trim / shoeing / observations…"
                  onChange={(e) => setEditingFarrier((s) => ({ ...s, notes: e.target.value }))}
                  data-testid="farrier-edit-notes"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="far-next-due">Farrier Next Due</Label>
                <Input
                  id="far-next-due"
                  type="date"
                  value={editingFarrier.next_due || ""}
                  onChange={(e) => setEditingFarrier((s) => ({ ...s, next_due: e.target.value }))}
                  data-testid="farrier-edit-next-due"
                />
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  {[42, 49, 56].map((n) => (
                    <Button
                      key={n}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2.5 text-xs font-mono"
                      onClick={() => setEditingFarrier((s) => ({ ...s, next_due: addDays(date, n) }))}
                      data-testid={`farrier-next-due-plus-${n}`}
                    >
                      +{n}d
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingFarrier(null)}>Cancel</Button>
            <Button onClick={saveFarrierEdit} disabled={savingFarrier} data-testid="farrier-edit-save-btn">
              {savingFarrier && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Breed date picker (auto-schedules Bred + Post-breed infuse) */}
      <Dialog open={breedOpen} onOpenChange={setBreedOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Schedule Breed Date</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            Marks the mare <strong>Bred</strong> (red) on this date and auto-adds a <strong>Post-breed infuse</strong> (blue) the next day.
          </p>
          <div className="space-y-1.5 py-1">
            <Label htmlFor="breed-date">Date bred</Label>
            <Input
              id="breed-date"
              type="date"
              value={breedDate}
              onChange={(e) => e.target.value && setBreedDate(e.target.value)}
              data-testid="breed-date-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBreedOpen(false)}>Skip</Button>
            <Button onClick={confirmBreed} disabled={ribbonBusy === breedMareId} data-testid="breed-confirm-btn" className="gap-2">
              {ribbonBusy === breedMareId ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Oxytocin dose entry */}
      <Dialog open={oxyOpen} onOpenChange={setOxyOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Oxytocin</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            Shows in Notes as <span className="font-mono">Oxytocin {oxyForm.amount || "1"}ml {oxyForm.route} x {oxyForm.times || "2"} ({oxyForm.days || "3"})</span>
          </p>
          <div className="grid grid-cols-2 gap-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="oxy-amount">Amount (ml)</Label>
              <Input id="oxy-amount" type="number" inputMode="decimal" value={oxyForm.amount}
                onChange={(e) => setOxyForm((s) => ({ ...s, amount: e.target.value }))}
                placeholder="e.g. 1" data-testid="oxy-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Route</Label>
              <div className="flex gap-1.5">
                {["IV", "IM"].map((rt) => (
                  <Button key={rt} type="button" variant={oxyForm.route === rt ? "default" : "outline"} size="sm"
                    className="h-9 flex-1" onClick={() => setOxyForm((s) => ({ ...s, route: rt }))}
                    data-testid={`oxy-route-${rt}`}>
                    {rt}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="oxy-times">Times given</Label>
              <Input id="oxy-times" type="number" inputMode="numeric" value={oxyForm.times}
                onChange={(e) => setOxyForm((s) => ({ ...s, times: e.target.value }))}
                placeholder="e.g. 2" data-testid="oxy-times" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="oxy-days">For how many days</Label>
              <Input id="oxy-days" type="number" inputMode="numeric" value={oxyForm.days}
                onChange={(e) => setOxyForm((s) => ({ ...s, days: e.target.value }))}
                placeholder="e.g. 3" data-testid="oxy-days" />
            </div>
          </div>
          <DialogFooter className="flex-row justify-between sm:justify-between">
            {(editing?.procedures || []).includes("Oxytocin") ? (
              <Button variant="ghost" className="text-destructive" onClick={removeOxy} data-testid="oxy-remove-btn">Remove</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOxyOpen(false)}>Cancel</Button>
              <Button onClick={saveOxy} data-testid="oxy-save-btn">Add to Notes</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bred / Procedures reports (date range) */}
      <Dialog open={!!reportKind} onOpenChange={(o) => !o && setReportKind(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{reportKind === "bred" ? "Cover Dates Report" : reportKind === "infoal" ? "In-Foal Season Summary" : reportKind === "status" ? "Mare Status Report" : reportKind === "breeding" ? "Breeding & Ovulation Schedule" : reportKind === "farrier" ? "Farrier Report (Foals)" : "Procedures Report"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">
            {reportKind === "bred"
              ? "Every mare's cover date, stallion and estimated due date within the range."
              : reportKind === "infoal"
              ? "End-of-season list of mares in foal with their last cover date and estimated due date (from the Mare Card)."
              : reportKind === "status"
              ? "A one-page snapshot of every active mare — last date checked and that day's findings — so you can see where everyone stands."
              : reportKind === "breeding"
              ? "Every mare that shows Bred in Daily Rounds — her most recent bred date, ovulation date & side (OV in the L/R Ovary field), and 14 / 28 / 45-day check dates counted from ovulation."
              : reportKind === "farrier"
              ? "Print the foals on the Farrier list for a chosen day, each showing their previous farrier visit date and notes — or list every foal due through a date."
              : "Lists every mare and date a procedure was recorded within the range."}
          </p>
          {(reportKind === "bred" || reportKind === "procedures") && (
            <div className="grid grid-cols-2 gap-4 py-1">
              <div className="space-y-1.5">
                <Label htmlFor="rep-start">From</Label>
                <Input id="rep-start" type="date" value={repStart} onChange={(e) => e.target.value && setRepStart(e.target.value)} data-testid="report-start-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rep-end">To</Label>
                <Input id="rep-end" type="date" value={repEnd} onChange={(e) => e.target.value && setRepEnd(e.target.value)} data-testid="report-end-input" />
              </div>
            </div>
          )}
          {reportKind === "procedures" && (
            <div className="space-y-1.5">
              <Label>Procedure</Label>
              <Select value={repProcedure} onValueChange={setRepProcedure}>
                <SelectTrigger data-testid="report-procedure-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All procedures</SelectItem>
                  {PROCEDURES.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {(reportKind === "bred" || reportKind === "infoal") && (
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="report-only-confirmed">
              <Checkbox checked={repOnlyConfirmed} onCheckedChange={(v) => setRepOnlyConfirmed(!!v)} data-testid="report-only-confirmed-checkbox" />
              <span className="text-sm">Only mares confirmed in foal</span>
            </label>
          )}
          {reportKind === "breeding" && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="breeding-asof">As-of date</Label>
                <Input id="breeding-asof" type="date" value={repBreedingAsOf} onChange={(e) => setRepBreedingAsOf(e.target.value)} data-testid="report-breeding-asof-input" />
                <span className="block text-xs text-muted-foreground">Checks before this date grey out as done; checks in that date&apos;s week highlight gold.</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="report-active-only">
                <Checkbox checked={repActiveOnly} onCheckedChange={(v) => setRepActiveOnly(!!v)} data-testid="report-active-only-checkbox" />
                <span className="text-sm">Active only — hide bred mares past their 14-day check that are not confirmed in foal</span>
              </label>
            </div>
          )}
          {reportKind === "farrier" && (
            <div className="space-y-3" data-testid="report-farrier-date">
              <div className="space-y-1.5">
                <Label htmlFor="farrier-date">{repFarrierScheduledOnly ? "Schedule date" : "Due through date"}</Label>
                <Input id="farrier-date" type="date" value={repFarrierDate} onChange={(e) => setRepFarrierDate(e.target.value)} data-testid="report-farrier-date-input" />
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={repFarrierScheduledOnly} onCheckedChange={(v) => setRepFarrierScheduledOnly(!!v)} data-testid="report-farrier-scheduled-checkbox" />
                <span>
                  Only foals on the Farrier list for this day
                  <span className="block text-xs text-muted-foreground">Pulls from the Farrier Daily Round list — manually added foals appear with their previous visit date &amp; notes. Uncheck to list every foal due through the date.</span>
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportKind(null)}>Cancel</Button>
            <Button onClick={runReport} disabled={repBusy} data-testid="report-generate-btn" className="gap-2">
              {repBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
